import React, { FC, useEffect, useState } from "react";
import { observer } from "mobx-react";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import DeleteCustomerPopup from "./DeleteCustomerPopup";
import { NavigateFunction } from "react-router-dom";
import {
  CustomPlaceholders,
  DashboardTopBar,
  ItemContentArea,
  NavPills,
  NewContactPopup,
  RelatedInvoicesGrid,
  RelatedOrdersGrid,
  RelatedPaymentMethodGrid,
  RelatedPaymentsGrid,
  RelatedSubscriptionGrid,
  RenderPaymentMethod,
  SplitDropDown,
} from "../../../shared/components";
import {
  CustomLabel,
  FlexContainer,
  Loader,
  NavigationButtons,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ContactStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { AuthService, ContactService } from "../../../shared/services";
import {
  DescriptionCell,
  ItemDetailLeftPanel,
  ItemDetailMainWrapper,
  ItemDetailRightPanel,
  LabelCell,
  MainOuterCard,
  SubscriptionStylesWrapper,
} from "../../../shared/styles";
import {
  ContactDetailModel,
  ContactReferenceModel,
  RelatedPaymentMethodGridModel,
  SelectedItemModel,
} from "../../../shared/models";
import {
  NewPaymentRelatedSource,
  SubscriptionStatus,
} from "../../../shared/enums";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import CartIcon from "../../../../../../images/placeholders/cart-grey-border.svg";

type IProps = {
  id: string;
  contactStore: ContactStore;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

const ContactDetailPage: FC<IProps> = ({
  id,
  contactStore,
  itemStore,
  navigate,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [showLoader, setShowLoader] = useState(true);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeletePopUp, setShowDeletePopUp] = useState(false);
  //THIS IS FOR NEW CONTACT CREATED FROM MINDME CONTACT DETAILS
  const [cId, setContactId] = useState(id || "");

  useEffect(() => {
    id && setShowLoader(true);
    onRefresh();
    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, []);

  function onRefresh(contactId?: string) {
    if (contactId && !cId) {
      setContactId(contactId);
    }
    if (!cId && !contactId) {
      setShowLoader(false);
      contactStore.deserializeDetailPage(null);
      return;
    }
    const newContactId = contactId || cId;
    ContactService.getContact(
      AuthService.authData.accountId,
      newContactId,
      contactStore.searchValue
    ).subscribe({
      next: (response) => {
        if (response.result) {
          contactStore.deserializeDetailPage(response.result);
          if (contactStore.detailPage?.detail) {
            contactStore.setSelectedContactDetails(
              contactStore.detailPage.detail
            );
          }
          setShowLoader(false);
          itemStore?.setBreadCrumbData(
            new SelectedItemModel({
              route: "/app/contacts",
              title:
                contactStore.detailPage?.detail?.fullName ||
                contactStore.detailPage?.detail?.homePhone?.phoneNumber ||
                "",
            })
          );
        }
      },
    });
  }

  if (!contactStore.detailPage?.detail)
    return <>{new Loader(true).loaderComponent}</>;

  const row = (
    label: string,
    rightColumn: string | React.ReactNode
  ): React.ReactNode => {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel>{label}</CustomLabel>
        </LabelCell>
        <DescriptionCell>{rightColumn}</DescriptionCell>
      </FlexContainer>
    );
  };
  const navPills = () => {
    return (
      <FlexContainer alignItems="center" padding="0px 0px 50px">
        <CustomLabel color="#2C3E50" fontSize="30px" fontWeight="600">
          Contact
        </CustomLabel>
        <div className="flex-auto-grow">
          {false && (
            <NavPills
              defaultActiveKey="details"
              pills={[
                {
                  key: "details",
                  title: "DETAILS",
                },
                {
                  key: "activity",
                  title: "ACTIVITY",
                },
              ]}
              onSelect={() => {}}
            />
          )}
        </div>
      </FlexContainer>
    );
  };

  const actionDropdown = () => {
    return (
      <SplitDropDown
        label="Edit"
        onClick={() => {
          if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
          setShowEditPopup(true);
        }}
        className="edit-dropdown-item"
        options={[
          {
            icon: EditIcon,
            label: "Edit",
            value: "edit",
            onClick: () => {
              if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
              setShowEditPopup(true);
            },
          },
          {
            icon: DeleteIcon,
            label: "Delete",
            value: "Delete",
            onClick: () => {
              if (!rolesAndPermissionStore.hasManagePermission("Delete"))
                return;
              setShowDeletePopUp(true);
            },
          },
        ]}
      />
    );
  };

  const onCloseDelete = (refresh: boolean) => {
    setShowDeletePopUp(false);
    if (refresh) {
      navigate("app/contacts");
    }
  };

  const onCloseEdit = (refresh: boolean) => {
    setShowEditPopup(false);
    if (refresh) {
      onRefresh();
    }
  };

  const detail = contactStore.detailPage.detail || new ContactDetailModel();
  const recentSubscriptions = contactStore.detailPage.subscriptions
    .filter((sub) => sub.status === SubscriptionStatus.Active)
    .sort(
      (a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    )
    .slice(0, 3);

  const relevantInvoices = contactStore.detailPage.invoices
    .filter(
      (invoice) => invoice.status === "Open" || invoice.status === "PastDue"
    )
    .sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );

  const nearestInvoice =
    relevantInvoices.length > 0 ? relevantInvoices[0] : null;

  function getLimitedPaymentMethods(
    paymentMethods: RelatedPaymentMethodGridModel[]
  ) {
    const primaryPayment = paymentMethods.find((p) => p.isPrimary);
    const nonPrimaryPayments = paymentMethods.filter((p) => !p.isPrimary);
    if (!primaryPayment) {
      return nonPrimaryPayments
        .slice(0, 3)
        .map((p, idx) => (
          <RenderPaymentMethod
            key={idx}
            paymentMethod={p.paymentMethod}
            showPriority={true}
          />
        ));
    }
    const limitedPayments = [primaryPayment, ...nonPrimaryPayments.slice(0, 2)];
    return limitedPayments.map(
      (p, idx) =>
        p && (
          <RenderPaymentMethod
            key={idx}
            paymentMethod={p.paymentMethod}
            showPriority={true}
          />
        )
    );
  }

  return (
    <>
      {!AuthService.isMindMeContactRoute && (
        <DashboardTopBar
          itemStore={itemStore}
          merchantAccountStore={merchantAccountStore}
          rolesAndPermissionStore={rolesAndPermissionStore}
          rightContent={
            <>
              <NavigationButtons
                currentIndex={contactStore.detailPage.index}
                totalItems={contactStore.detailPage.totalContacts}
                previousRecordId={contactStore.detailPage.previousRecordId}
                nextRecordId={contactStore.detailPage.nextRecordId}
                onNavigate={(id) => navigate(`/app/contacts/detail/${id}`)}
              />
              {actionDropdown()}
            </>
          }
        />
      )}
      <ItemContentArea
        showLoader={showLoader}
        itemStore={itemStore}
        onlyShowNotificationBox={AuthService.isMindMeContactRoute}
        showMerchantAccountNotificationBar={
          merchantAccountStore.hasNotificationBar() &&
          !AuthService.isMindMeContactRoute
        }
        node={
          <ItemDetailMainWrapper>
            <SubscriptionStylesWrapper>
              {cId ? (
                <MainOuterCard>
                  {navPills()}
                  <FlexContainer>
                    <ItemDetailLeftPanel>
                      {row("Contact ID", detail.id || "")}
                      {row(
                        "Created Date",
                        UtilityMethods.formatDateInString(detail.createdDate)
                      )}
                      {row("Created By", detail.createdBy || "-")}
                      {detail.homePhone &&
                        row("Home", detail.homePhone?.phoneNumber || "")}
                      {detail.workPhone &&
                        row("Work", detail.workPhone?.phoneNumber || "")}
                      {detail.homeEmail && row("Email", detail.homeEmail)}
                      {detail.workEmail && row("Work", detail.workEmail)}
                      {/* {row("DOB", "-")}
                    {row("Taxable", "Yes")} */}
                      {/* {row(
                      "Related",
                      <>
                        <FlexContainer
                          justifyContent="space-between"
                          className="related-label"
                        >
                          <div>
                            <CustomLabel>Frank Hawthorne</CustomLabel>
                          </div>
                          <div>
                            <CustomLabel>Spouse</CustomLabel>
                          </div>
                        </FlexContainer>
                        <FlexContainer
                          justifyContent="space-between"
                          className="related-label"
                        >
                          <div>
                            <CustomLabel>Bert Hawthorne</CustomLabel>
                          </div>
                          <div>
                            <CustomLabel>Son</CustomLabel>
                          </div>
                        </FlexContainer>
                      </>
                    )} */}
                    </ItemDetailLeftPanel>
                    <ItemDetailRightPanel>
                      <div className="blue-label-container span-semi-bold">
                        {row(
                          "Account Credits",
                          <span>
                            {"$" +
                              AccountSettingsStore.formatAmount(
                                detail.accountCredits,
                                true
                              )}
                          </span>
                        )}
                        {row(
                          "Subscriptions",
                          <FlexContainer fxLayout="column">
                            {recentSubscriptions.length > 0 ? (
                              recentSubscriptions.map((sub, index) => (
                                <CustomLabel key={index}>
                                  <span className="highlighted-black">{`$${AccountSettingsStore.formatAmount(sub.amount, true)} `}</span>
                                  recurs every {sub.billingIntervalCount}{" "}
                                  {sub.billingInterval +
                                    (sub.billingIntervalCount > 1 ? "s" : "")}
                                  <br />
                                </CustomLabel>
                              ))
                            ) : (
                              <CustomLabel>No Active Subscriptions</CustomLabel>
                            )}
                          </FlexContainer>
                        )}
                        {row(
                          "Next Invoice",
                          nearestInvoice ? (
                            <CustomLabel>
                              <span className="highlighted-black">{`$${AccountSettingsStore.formatAmount(nearestInvoice.netAmount, true)} `}</span>
                              next due
                              <span>
                                {" "}
                                {UtilityMethods.formatDateInString(
                                  nearestInvoice.dueDate
                                )}
                              </span>
                            </CustomLabel>
                          ) : (
                            <CustomLabel>-</CustomLabel>
                          )
                        )}
                        {row(
                          "Past Due",
                          detail.pastDueFrom ? (
                            <CustomLabel>
                              <span className="red-text">
                                $
                                {AccountSettingsStore.formatAmount(
                                  detail.pastDue,
                                  true
                                )}{" "}
                              </span>
                              due since
                              <span className="red-text">
                                {" "}
                                {UtilityMethods.formatDateInString(
                                  detail.pastDueFrom
                                )}
                              </span>
                            </CustomLabel>
                          ) : (
                            <CustomLabel>-</CustomLabel>
                          )
                        )}
                        {/* {row(
                        "Disputes",
                        <CustomLabel>
                          <span className="deep-purple">2</span> payments
                          disputed <span className="deep-purple">$300.00</span>{" "}
                          <br />
                          Last Dispute on{" "}
                          <span className="deep-purple">
                            Feb 15, 2024{" "}
                          </span>{" "}
                          <br />
                          Response due in{" "}
                          <span className="deep-purple">15 days</span>
                        </CustomLabel>
                      )} */}
                        {contactStore.detailPage.detail.payments &&
                          row(
                            "Payments",
                            <CustomLabel>
                              <span className="highlighted-black">
                                $
                                {AccountSettingsStore.formatAmount(
                                  contactStore.detailPage.detail.payments
                                    ?.allTimes
                                )}
                              </span>{" "}
                              all time
                              <br />
                              <span>
                                {" "}
                                $
                                {AccountSettingsStore.formatAmount(
                                  contactStore.detailPage.detail.payments
                                    ?.totalInYear
                                )}
                              </span>{" "}
                              in{" "}
                              {
                                contactStore.detailPage.detail.payments
                                  ?.currentYear
                              }
                              <br />
                              <span>
                                $
                                {AccountSettingsStore.formatAmount(
                                  contactStore.detailPage.detail.payments
                                    ?.totalInCurrentMonth
                                )}
                              </span>{" "}
                              in{" "}
                              {
                                contactStore.detailPage.detail.payments
                                  ?.currentMonth
                              }
                            </CustomLabel>
                          )}
                        {contactStore.detailPage.paymentMethods &&
                          row(
                            "Payment Method",
                            <FlexContainer fxLayout="column" spacing={10}>
                              {getLimitedPaymentMethods(
                                contactStore.detailPage.paymentMethods
                              )}
                            </FlexContainer>
                          )}
                      </div>
                    </ItemDetailRightPanel>
                  </FlexContainer>
                </MainOuterCard>
              ) : (
                <MainOuterCard className="contact-payment-plh">
                  <CustomPlaceholders
                    // height={"100vh"}
                    image={CartIcon}
                    label={"No Purchases"}
                    description={
                      <CustomLabel color="#aaa" textAlign="center">
                        There are no Orders, Invoices, Subscriptions or Payments
                        <br />
                        for this Contact. Click below to create one.
                      </CustomLabel>
                    }
                    showItems={false}
                  />
                </MainOuterCard>
              )}
              <RelatedOrdersGrid orders={contactStore.detailPage.orders} />
              <RelatedInvoicesGrid
                disableNewButton={AuthService.isMindMeContactRoute && !cId}
                invoices={contactStore.detailPage.invoices}
                onUpdate={() => onRefresh()}
                contact={contactStore.detailPage.detail.createContactRefrenceModel()}
                source={NewPaymentRelatedSource.Invoice}
                sourceId={contactStore.detailPage.detail.id}
                rolesAndPermissionStore={rolesAndPermissionStore}
              />
              <RelatedSubscriptionGrid
                disableNewButton={AuthService.isMindMeContactRoute && !cId}
                subscriptions={contactStore.detailPage.subscriptions}
                selectedContact={
                  contactStore.selectedContactDetails ||
                  new ContactReferenceModel()
                }
                onUpdate={() => onRefresh()}
                rolesAndPermissionStore={rolesAndPermissionStore}
              />
              <RelatedPaymentsGrid
                payments={contactStore.detailPage.payments}
                onUpdate={(contactId?: string) => onRefresh(contactId)}
                contact={contactStore.detailPage.detail.createContactRefrenceModel()}
                source={NewPaymentRelatedSource.Invoice}
                sourceId={contactStore.detailPage.detail.id}
                rolesAndPermissionStore={rolesAndPermissionStore}
              />
              <RelatedPaymentMethodGrid
                disableNewButton={AuthService.isMindMeContactRoute && !cId}
                paymentMethods={contactStore.detailPage.paymentMethods}
                onUpdate={() => onRefresh()}
                contact={contactStore.detailPage.detail.createContactRefrenceModel()}
                source={NewPaymentRelatedSource.Invoice}
                sourceId={contactStore.detailPage.detail.id}
                rolesAndPermissionStore={rolesAndPermissionStore}
              />
            </SubscriptionStylesWrapper>
          </ItemDetailMainWrapper>
        }
      ></ItemContentArea>
      {showDeletePopUp && (
        <DeleteCustomerPopup
          onClose={(refresh: boolean) => onCloseDelete(refresh)}
          contactsData={contactStore.detailPage}
        />
      )}
      {showEditPopup && (
        <NewContactPopup
          onClose={(refresh: boolean) => onCloseEdit(refresh)}
          isEdit={true}
          contactId={detail.id}
        />
      )}
    </>
  );
};

export default withRouter(observer(ContactDetailPage));
